import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DiMcApiUrl } from '@di';
import type {
  GsmDocumentCount,
  GsmDocumentCountBody,
  GsmDocumentCountResponse,
  GsmProduct,
  GsmProductResponse,
} from '@models/gsm.model';
import { FoerchGsmLocalStorageService } from '@services/foerch-gsm-local-storage.service';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * micronaut /Micro Service
 */
@Injectable()
export class FoerchMsService {
  private readonly _apiUrl = inject(DiMcApiUrl);
  private readonly _http = inject(HttpClient);
  private readonly _localStorageService = inject(FoerchGsmLocalStorageService);
  private readonly _router = inject(Router);

  /**
   * get all custom (Fremd) articles
   */
  getCustomArticles(query?: string): Observable<GsmProductResponse> {
    const userId = this._localStorageService.getUserUnitId();
    const params = query ? query : 'pageNumber=0&pageSize=10';
    const url = `${this._apiUrl}/${userId}/product?${params}`;

    return this._http.get<any>(url, { headers: this.getAccessHeaders() }).pipe(
      map(response => {
        const results: GsmProduct[] = (response.results || []).map(product => ({
          ...product,
          foerchDocuments: (product.foerchDocuments || []).map(doc => ({
            ...doc,
            abbreviation:
              doc.abbreviation === 'REQUEST' ? 'Anfrage' : doc.abbreviation,
          })),
        }));

        return {
          ...response,
          results,
        };
      }),
      catchError(err => {
        if (err && (err.status === 500 || err.status === 0)) {
          return of(null);
        }
        this._localStorageService.clearSessionState();
        this._router.navigateByUrl('/login');
        return of(null);
      })
    );
  }

  getCustomArticle(articleId: string): Observable<GsmProduct> {
    const userId = this._localStorageService.getUserUnitId();
    const url = `${this._apiUrl}/${userId}/product/${articleId}`;
    return this._http.get<any>(url, { headers: this.getAccessHeaders() });
  }

  getAllManufacturer(): Observable<string[]> {
    const userId = this._localStorageService.getUserUnitId();
    const url = `${this._apiUrl}/${userId}/manufacturer`;
    return this._http.get<any>(url, { headers: this.getAccessHeaders() });
  }

  countDocumentUsage(articleId: string, type: string): Observable<void> {
    const userId = this._localStorageService.getUserUnitId();
    const url = `${this._apiUrl}/${userId}/product/${articleId}/download`;
    return this._http.post<any>(
      url,
      {
        customerCode: userId,
        productCode: articleId,
        type,
        downloadTime: new Date(),
      },
      { headers: this.getAccessHeaders() }
    );
  }

  countAvailableDocuments(
    body: GsmDocumentCountBody
  ): Observable<GsmDocumentCount> {
    const b2bUnitId = this._localStorageService.getUserUnitId();
    const url = `${this._apiUrl}/${b2bUnitId}/documents/count`;

    return this._http
      .post<GsmDocumentCountResponse>(url, body, {
        headers: this.getAccessHeaders(),
      })
      .pipe(map(response => response?.result));
  }

  private getAccessHeaders(): HttpHeaders {
    const accessToken = this._localStorageService.getAccessToken();

    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });
  }
}
